import { z } from 'zod'

import { RegionSchema } from '~/@entities/Region'
import {
  AllGradeSubjectSchema,
  type GradeMiddle,
  HighSchoolSubjectSchema,
  RevisionSchema,
  SchoolTypeSchema,
  SemesterSchema,
} from '~/@entities/SchoolSystem'
import { WorkbookSecuringTypeSchema } from '~/@entities/Workbook'

// 기출 시험지가 있는 중학교 학년. 고등은 학년 자리에 과목이 온다
export const SCHOOL_TEST_MIDDLE_GRADE = ['1', '2', '3'] as const satisfies readonly GradeMiddle[]

/***************** 시험 종류 *****************/
export const SCHOOL_TEST_TYPE = ['MIDTERM', 'FINAL'] as const

/***************** 기출 제공 범위 *****************/
// 원본 PDF·유사 문제 중 무엇을 제공하는가
export const SCHOOL_TEST_PROVIDE_TYPE = ['ALL', 'PDF_ONLY', 'SIMILAR_ONLY', 'NONE'] as const

/***************** 기출 올린 주체 *****************/
export const SCHOOL_TEST_UPLOADER_TYPE = ['MATHFLAT', 'CUSTOMER'] as const

/***************** 학교 게시 상태 *****************/
// 서버 ContentStatus
export const SCHOOL_STATUS = ['DRAFT', 'ACTIVE', 'INACTIVE', 'HIDDEN'] as const

export const SchoolTestTypeSchema = z.enum(SCHOOL_TEST_TYPE)
export type SchoolTestType = z.infer<typeof SchoolTestTypeSchema>

export const SchoolTestProvideTypeSchema = z.enum(SCHOOL_TEST_PROVIDE_TYPE)
export type SchoolTestProvideType = z.infer<typeof SchoolTestProvideTypeSchema>

export const SchoolTestUploaderTypeSchema = z.enum(SCHOOL_TEST_UPLOADER_TYPE)
export type SchoolTestUploaderType = z.infer<typeof SchoolTestUploaderTypeSchema>

export const SchoolStatusSchema = z.enum(SCHOOL_STATUS)
export type SchoolStatus = z.infer<typeof SchoolStatusSchema>

// 기출은 중·고등에만 있다
const SchoolTestSchoolTypeSchema = SchoolTypeSchema.exclude(['ELEMENTARY'])

// 기출을 제공하는 학교 한 곳(/school-tests/schools). 서버가 함께 내는 uploaderType 은 앱이 읽지 않아 받지 않는다.
export const SchoolTestSchoolSchema = z.object({
  code: z.string(),
  name: z.string(),
  type: SchoolTypeSchema,
  regionCode: z.string(),
  priority: z.number().nullable(),
  status: SchoolStatusSchema,
  // 하위호환으로 남은 값. 3개년 데이터로 뽑은 provideTypes 가 정확하므로 그쪽을 쓴다.
  provideType: SchoolTestProvideTypeSchema.nullable(),
  provideTypes: z.array(SchoolTestProvideTypeSchema),
  securingTypes: z.array(WorkbookSecuringTypeSchema),
})
export type SchoolTestSchool = z.infer<typeof SchoolTestSchoolSchema>

// 학교 검색(/schools/all)의 한 곳. 초등학교도 실린다. 서버가 지역에 함께 내는 priority(정렬 순서)는 앱이 읽지
// 않아 받지 않는다. 학교 priority·시군구 이름은 nullable 이다(시도 행은 시군구 이름이 없다). 2026-10-13 백엔드
// develop(cc5f60e17) 의 schoolTest/controller/response/SchoolWithRegionResponse.kt·model/dto/RegionDto.kt 로 확인.
export const SchoolSchema = z.object({
  code: z.string(),
  name: z.string(),
  type: SchoolTypeSchema,
  priority: z.number().nullable(),
  status: SchoolStatusSchema,
  region: RegionSchema,
})
export type School = z.infer<typeof SchoolSchema>

// 시도 > 시군구 > 학교로 묶은 기출 제공 학교
export const SchoolTestSchoolsBySidoSchema = z.object({
  code: z.string(),
  sidoName: z.string(),
  type: z.literal('SIDO'),
  child: z.array(
    z.object({
      code: z.string(),
      parentCode: z.string(),
      // 서버 선언은 String? 이지만 시군구 행에는 이름이 있다(null 은 시도 행만)
      sigunguName: z.string(),
      type: z.literal('SIGUNGU'),
      child: z.array(SchoolTestSchoolSchema),
    }),
  ),
})
export type SchoolTestSchoolsBySido = z.infer<typeof SchoolTestSchoolsBySidoSchema>

// 한 학교의 같은 시험 종류(중간·기말) 기출 묶음
export const SchoolTestIdsByTypeSchema = z.object({
  ids: z.array(z.number()),
  type: SchoolTestTypeSchema,
  securingTypes: z.array(WorkbookSecuringTypeSchema),
})
export type SchoolTestIdsByType = z.infer<typeof SchoolTestIdsByTypeSchema>

// 시도 > 시군구 > 학교 > 시험 종류로 묶은 기출(/school-tests/group-by-school)
export const SchoolTestsBySidoSchema = z.object({
  code: z.string(),
  name: z.string(),
  sigunguRegions: z.array(
    z.object({
      code: z.string(),
      name: z.string(),
      schools: z.array(
        z.object({
          code: z.string(),
          name: z.string(),
          schoolTests: z.array(SchoolTestIdsByTypeSchema),
        }),
      ),
    }),
  ),
})
export type SchoolTestsBySido = z.infer<typeof SchoolTestsBySidoSchema>
export type SchoolTestsBySigungu = SchoolTestsBySido['sigunguRegions'][number]
export type SchoolTestsBySchool = SchoolTestsBySigungu['schools'][number]

// 기출 시험지 원본(서버 SchoolTestResponse). 교재처럼 다룬다.
export const SchoolTestOriginSchema = z.object({
  id: z.number(),
  title: z.string(),
  subTitle: z.string(),
  year: z.number(),
  grade: AllGradeSubjectSchema,
  semester: SemesterSchema,
  // 고등 과목의 시행 학년·학기
  enforcementGrade: z.number(),
  enforcementSemester: SemesterSchema.unwrap(),
  schoolTestType: SchoolTestTypeSchema,
  uploaderType: SchoolTestUploaderTypeSchema,
  provideType: SchoolTestProvideTypeSchema,
  securingType: WorkbookSecuringTypeSchema,
  maxPage: z.number(),
  createDatetime: z.iso.datetime({ local: true }),
  region: RegionSchema,
  school: z.object({
    code: z.string(),
    name: z.string(),
    type: SchoolTestSchoolTypeSchema,
    priority: z.number().nullable(),
    status: SchoolStatusSchema,
  }),
  pdfDownloaded: z.boolean().nullable(),
})
export type SchoolTestOrigin = z.infer<typeof SchoolTestOriginSchema>

// 학교·연도·학년(고등은 과목)·학기·시험 종류로 정해지는 기출 한 회차(/school-tests/v2/page). 원본 시험지를 수급하지
// 못한 회차도 실리고, 그때 schoolTestWorkbook 이 null 이다. 서버가 지역에 함께 내는 priority 는 받지 않는다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 schoolTest/controller/response/SchoolTestV2Response.kt 로 확인.
const _SchoolTestRoundSchema = z.object({
  schoolCode: z.string(),
  schoolName: z.string(),
  revision: RevisionSchema,
  region: RegionSchema,
  schoolTestYear: z.number(),
  semester: SemesterSchema,
  schoolTestType: SchoolTestTypeSchema,
  schoolTestWorkbook: SchoolTestOriginSchema.nullable(),
})

// 중학교는 학년이 숫자 문자열이고, 고등학교는 학년 자리에 과목이 오며 학기가 없다(null).
export const SchoolTestSchema = z.discriminatedUnion('schoolType', [
  _SchoolTestRoundSchema.extend({
    schoolType: z.literal('MIDDLE'),
    grade: z.enum(SCHOOL_TEST_MIDDLE_GRADE),
  }),
  _SchoolTestRoundSchema.extend({
    schoolType: z.literal('HIGH'),
    grade: HighSchoolSubjectSchema,
  }),
])
export type SchoolTest = z.infer<typeof SchoolTestSchema>

// 이번 달 기출 PDF 내려받기 한도
export const SchoolTestPdfQuotaSchema = z.object({
  yearMonth: z.string().regex(/^\d{4}-\d{2}$/),
  pdfDownloadedCount: z.number(),
  pdfDownloadLimit: z.number(),
})
export type SchoolTestPdfQuota = z.infer<typeof SchoolTestPdfQuotaSchema>

// 기출 PDF 를 내려받거나 미리볼 주소. 고른 회차가 여럿이면 서버가 하나로 묶은 파일의 주소를 준다
export const SchoolTestFileSchema = z.object({
  presignedUrl: z.string(),
  fileName: z.string(),
})
export type SchoolTestFile = z.infer<typeof SchoolTestFileSchema>

// 이 학원이 최근 기출을 내려받은 학교. 최근 순이다
export const RecentlyDownloadedSchoolSchema = z.object({
  schoolCode: z.string(),
  schoolName: z.string(),
})
export type RecentlyDownloadedSchool = z.infer<typeof RecentlyDownloadedSchoolSchema>
